/**
 * ReportView - Displays the full answer report at end of assessment
 * Systembolaget-inspired clean, minimal design
 */

import React from 'react';
import { colors, fonts, spacing, radii } from '../styles/theme';
import { ReportItem } from '../types';

interface ReportViewProps {
    items: ReportItem[];
    phase: string;
    area: string;
}

const ReportView: React.FC<ReportViewProps> = ({ items, phase, area }) => {
    // Count by answer type
    const counts = items.reduce(
        (acc, item) => {
            acc[item.value] = (acc[item.value] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );

    const enablingYesItems = items.filter(
        (item) => item.value === 'yes' && item.enabling === true
    );

    const nonEnablingItems = items.filter(
        (item) => !(item.value === 'yes' && item.enabling === true)
    );

    return (
        <div style={styles.container}>
            {enablingYesItems.length > 0 && (
                <section style={styles.enablingCard} aria-labelledby="enabling-report-heading">
                    <h2 id="enabling-report-heading" style={styles.enablingHeading}>
                        Enabling cards (yes answers)
                    </h2>
                    <p style={styles.enablingIntro}>
                        You have answered yes to questions that indicates that your VR can contribute to improving the delivery of service or that it has the potential to support the protection of human rights. Use them to support your application
                    </p>
                    <ul style={styles.enablingList}>
                        {enablingYesItems.map((item) => (
                            <li key={item.questionId} style={styles.enablingListItem}>
                                <span style={styles.itemId}>{item.questionId}</span>
                                <span style={styles.itemQuestion}>{item.question}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Summary stats */}
            <div style={styles.summary}>
                <div style={styles.stat}>
                    <span style={styles.statCount}>{items.length}</span>
                    <span style={styles.statLabel}>Total</span>
                </div>
                <div style={styles.statDivider} />
                <div style={styles.stat}>
                    <span style={{ ...styles.statCount, color: colors.yes }}>{counts['yes'] || 0}</span>
                    <span style={styles.statLabel}>Yes</span>
                </div>
                <div style={styles.stat}>
                    <span style={{ ...styles.statCount, color: colors.no }}>{counts['no'] || 0}</span>
                    <span style={styles.statLabel}>No</span>
                </div>
                <div style={styles.stat}>
                    <span style={{ ...styles.statCount, color: colors.cart }}>{counts['add-to-cart'] || 0}</span>
                    <span style={styles.statLabel}>Cart</span>
                </div>
            </div>

            {/* Report list (excluding enabling yes items shown above) */}
            <div style={styles.list}>
                {nonEnablingItems.map((item) => (
                    <div key={item.questionId} style={styles.item}>
                        <div style={styles.itemLeft}>
                            <span style={styles.itemId}>{item.questionId}</span>
                            <span style={styles.itemQuestion}>{item.question}</span>
                        </div>
                        <span style={getAnswerBadgeStyle(item.value)}>
                            {formatAnswer(item.value)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

function formatAnswer(value: string): string {
    switch (value) {
        case 'yes':
            return 'Yes';
        case 'no':
            return 'No';
        case 'add-to-cart':
            return 'Cart';
        default:
            return value;
    }
}

function getAnswerBadgeStyle(value: string): React.CSSProperties {
    const base: React.CSSProperties = {
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        padding: `${spacing[1]} ${spacing[3]}`,
        borderRadius: radii.full,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        flexShrink: 0,
    };

    switch (value) {
        case 'yes':
            return { ...base, backgroundColor: '#dcfce7', color: colors.yes };
        case 'no':
            return { ...base, backgroundColor: '#fee2e2', color: colors.no };
        case 'add-to-cart':
            return { ...base, backgroundColor: '#fef3c7', color: colors.cart };
        default:
            return base;
    }
}

const styles: Record<string, React.CSSProperties> = {
    container: {},
    enablingCard: {
        marginBottom: spacing[6],
        padding: spacing[5],
        backgroundColor: colors.accentLight,
        border: `1px solid ${colors.accent}`,
        borderRadius: radii.lg,
    },
    enablingHeading: {
        margin: `0 0 ${spacing[2]} 0`,
        fontSize: fonts.lg,
        fontWeight: fonts.semibold,
        color: colors.text,
        lineHeight: fonts.lineHeightNormal,
        textAlign: 'center',
    },
    enablingIntro: {
        margin: `0 0 ${spacing[4]} 0`,
        fontSize: fonts.sm,
        fontWeight: fonts.normal,
        fontStyle: 'italic',
        color: colors.textSecondary,
        lineHeight: fonts.lineHeightRelaxed,
        textAlign: 'center',
    },
    enablingList: {
        margin: 0,
        paddingLeft: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
    },
    enablingListItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing[3],
        fontSize: fonts.sm,
        color: colors.text,
        lineHeight: fonts.lineHeightRelaxed,
    },
    summary: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[6],
        marginBottom: spacing[6],
        padding: spacing[4],
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
    },
    stat: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: spacing[1],
    },
    statCount: {
        fontSize: fonts['2xl'],
        fontWeight: fonts.bold,
        color: colors.text,
        lineHeight: 1,
    },
    statLabel: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    statDivider: {
        width: '1px',
        height: '40px',
        backgroundColor: colors.border,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[2],
    },
    item: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: spacing[4],
        padding: spacing[4],
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
    },
    itemLeft: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing[3],
        flex: 1,
        minWidth: 0,
    },
    itemId: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        fontFamily: 'monospace',
        flexShrink: 0,
        paddingTop: '2px',
    },
    itemQuestion: {
        fontSize: fonts.sm,
        color: colors.text,
        lineHeight: fonts.lineHeightNormal,
    },
};

export default ReportView;